/**
 * §11.8 Legal Policies — placeholder cards for future /trust/* routes.
 *
 * Per the Phase 11 PRD, each policy is a placeholder card that links
 * to a future canonical policy page.  The cards render an
 * "Available on request" badge until the underlying policy is
 * published — so we never link out to a 404 or a shell page.
 */

import React from 'react'
import clsx from 'clsx'
import Link from '@/shims/NextLink'
import {
  ScrollText, Lock, FileSignature, ShieldAlert, Cookie, Users,
  Sparkles, ArrowUpRight,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { LEGAL_POLICIES } from '../data'
import { TONE } from '../_shared'

const ICONS = { ScrollText, Lock, FileSignature, ShieldAlert, Cookie, Users, Sparkles }

export default function LegalPolicies() {
  return (
    <section id="legal" className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow tone="neutral">Legal</Eyebrow>
          <SectionHeading align="center"
            title={<>Policies that{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">govern the platform.</span></>}
            subtitle="The commercial, privacy and acceptable-use terms under which Kalki Ai Cloud is offered.  The full text of each policy is available on request while these documents finalise their public review."
          />
        </div>

        <div className="grid gap-3 md:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {LEGAL_POLICIES.map((p) => {
            const tone = TONE[p.tone] || TONE.orange
            const Icon = ICONS[p.iconKey] || Sparkles
            return (
              <Link key={p.id} href={p.href} className={clsx(
                'group relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 backdrop-blur-xl overflow-hidden',
                'transition-all hover:border-white/25 hover:bg-white/[0.04] hover:-translate-y-[1px]',
              )}>
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.10), transparent 70%)` }} />
                <div className="relative flex flex-col gap-4">
                  <div className="flex items-start justify-between gap-3">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border"
                      style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                      <Icon strokeWidth={1.8} style={{ width: 20, height: 20 }} />
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-0.5 text-[10.5px] font-semibold text-zinc-300">
                      Available on request
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      {p.label}
                      <ArrowUpRight className="h-4 w-4 text-zinc-500 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </h3>
                    <p className="text-sm text-zinc-400 leading-relaxed mt-2">{p.body}</p>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
